/**
 * StatusBadge — color-coded status badge for title lifecycle + publisher approval.
 *
 * Title lifecycle variants:  published | pending | draft | editing
 * Publisher approval states: approved | suspended | rejected
 *
 * Design spec:
 *   - pill shape (border-radius: 99px)
 *   - 9.5px / 700 / uppercase / 0.07em letter-spacing
 *   - 3px vertical / 10px horizontal padding
 *   - 5×5px dot indicator = currentColor
 *
 * @layer-1 — shared across all features
 */
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/utils";
import type { ApprovalStatus } from "@/shared/types/api";

/** Full badge status union — superset of ApprovalStatus. */
export type BadgeStatus =
  | ApprovalStatus
  | "published"
  | "draft"
  | "editing"
  | "scheduled"
  | "archived"
  /* Service-request lifecycle (rejected already covered by ApprovalStatus) */
  | "requested"
  | "quoted"
  | "paid"
  | "declined";

interface StatusBadgeProps {
  status: BadgeStatus;
  /**
   * Override the rendered label. Pass this when the label lives outside the
   * default "titles" namespace (e.g. service requests resolve their own copy).
   */
  label?: string;
  className?: string;
}

const STATUS_MAP: Record<BadgeStatus, { labelKey: string; className: string }> = {
  /* Title lifecycle */
  published: {
    labelKey: "status.published",
    className:
      "bg-[var(--published-bg)] text-[var(--published-text)]",
  },
  approved: {
    labelKey: "status.approved",
    className:
      "bg-[var(--published-bg)] text-[var(--published-text)]",
  },
  pending: {
    labelKey: "status.pending",
    className:
      "bg-[var(--pending-bg)] text-[var(--pending-text)]",
  },
  draft: {
    labelKey: "status.draft",
    className:
      "bg-[var(--draft-bg)] text-[var(--draft-text)]",
  },
  editing: {
    labelKey: "status.editing",
    className:
      "bg-[var(--color-info-bg)] text-[var(--color-info-text)]",
  },
  /* Legal lifecycle */
  scheduled: {
    labelKey: "status.scheduled",
    className:
      "bg-[var(--color-info-bg)] text-[var(--color-info-text)]",
  },
  archived: {
    labelKey: "status.archived",
    className:
      "bg-[var(--draft-bg)] text-[var(--text-tertiary)]",
  },
  /* Publisher approval */
  suspended: {
    labelKey: "status.suspended",
    className:
      "bg-[var(--color-error-bg)] text-[var(--color-error-text)]",
  },
  rejected: {
    labelKey: "status.rejected",
    className:
      "bg-[var(--color-error-bg)] text-[var(--color-error-text)]",
  },
  /* Service-request lifecycle — callers pass `label` (services namespace) */
  requested: {
    labelKey: "status.pending",
    className:
      "bg-[var(--pending-bg)] text-[var(--pending-text)]",
  },
  quoted: {
    labelKey: "status.editing",
    className:
      "bg-[var(--color-info-bg)] text-[var(--color-info-text)]",
  },
  paid: {
    labelKey: "status.published",
    className:
      "bg-[var(--published-bg)] text-[var(--published-text)]",
  },
  declined: {
    labelKey: "status.draft",
    className:
      "bg-[var(--draft-bg)] text-[var(--draft-text)]",
  },
};

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const { t } = useTranslation("titles");
  const entry = STATUS_MAP[status] ?? STATUS_MAP.pending!;

  return (
    <span
      className={cn(
        /* Design spec: pill, 9.5px 700 uppercase 0.07em, 3px 10px padding */
        "inline-flex items-center gap-[5px]",
        "rounded-[99px] px-[10px] py-[3px]",
        "text-[9.5px] font-bold uppercase tracking-[0.07em]",
        "whitespace-nowrap",
        entry.className,
        className
      )}
    >
      {/* 5×5px dot — color = currentColor */}
      <span className="size-[5px] rounded-full bg-current shrink-0" />
      {label ?? t(entry.labelKey)}
    </span>
  );
}
